import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import { clearTypedCosts, createEmptySurchargeSession, hasTypedCost } from './surchargeGridsModel';
import type {
  SeasonGridKey,
  SeasonLabel,
  SurchargePeriod,
  SurchargePeriodFormValues,
  SurchargeSessionState,
  Weekday,
} from './types';

export function useSurchargeGrids(offerId: string | undefined, currencyCode: string | undefined) {
  const [session, setSession] = useState<SurchargeSessionState>(createEmptySurchargeSession);
  const { enqueueSnackbar } = useSnackbar();
  const previousCurrencyCode = useRef(currencyCode);

  useEffect(() => {
    setSession(createEmptySurchargeSession());
  }, [offerId]);

  useEffect(() => {
    // Load-bearing: session is a dependency, so without this the effect would clear on every
    // keystroke.
    if (previousCurrencyCode.current === currencyCode) {
      return;
    }
    previousCurrencyCode.current = currencyCode;

    if (hasTypedCost(session)) {
      enqueueSnackbar('Typed costs cleared for the new currency', { variant: 'info' });
    }
    setSession(clearTypedCosts);
  }, [currencyCode, session, enqueueSnackbar]);

  const setSell = useCallback((seasonKey: SeasonGridKey, weekday: Weekday, sellAud: number | null) => {
    const amount = Math.max(0, sellAud ?? 0);
    setSession((previous) => ({
      ...previous,
      grids: { ...previous.grids, [seasonKey]: { ...previous.grids[seasonKey], [weekday]: amount } },
    }));
  }, []);

  const setCost = useCallback((seasonKey: SeasonGridKey, weekday: Weekday, costLocal: number | null) => {
    const amount = costLocal == null ? null : Math.max(0, costLocal);
    setSession((previous) => ({
      ...previous,
      costs: { ...previous.costs, [seasonKey]: { ...previous.costs[seasonKey], [weekday]: amount } },
    }));
  }, []);

  const addPeriod = useCallback((values: SurchargePeriodFormValues) => {
    const period: SurchargePeriod = { ...values, id: crypto.randomUUID() };
    setSession((previous) => ({
      ...previous,
      grids: { ...previous.grids, periods: [...previous.grids.periods, period] },
    }));
  }, []);

  const updatePeriod = useCallback((id: string, values: SurchargePeriodFormValues) => {
    setSession((previous) => ({
      ...previous,
      grids: {
        ...previous.grids,
        periods: previous.grids.periods.map((period) => (period.id === id ? { ...values, id } : period)),
      },
    }));
  }, []);

  const replacePeriods = useCallback((periods: Array<SurchargePeriodFormValues>) => {
    setSession((previous) => ({
      ...previous,
      grids: { ...previous.grids, periods: periods.map((values) => ({ ...values, id: crypto.randomUUID() })) },
    }));
  }, []);

  const removePeriod = useCallback((id: string) => {
    setSession((previous) => ({
      ...previous,
      grids: { ...previous.grids, periods: previous.grids.periods.filter((period) => period.id !== id) },
    }));
  }, []);

  const setMonthSeason = useCallback((monthKey: string, season: SeasonLabel | null) => {
    setSession((previous) => {
      const seasonByMonth = { ...previous.grids.seasonByMonth };
      if (season == null) {
        delete seasonByMonth[monthKey];
      } else {
        seasonByMonth[monthKey] = season;
      }

      return { ...previous, grids: { ...previous.grids, seasonByMonth } };
    });
  }, []);

  // Seeds the currency effect so restoring a draft doesn't clear its costs.
  const loadDraft = useCallback((draftSession: SurchargeSessionState, draftCurrencyCode: string | undefined) => {
    previousCurrencyCode.current = draftCurrencyCode;
    setSession(draftSession);
  }, []);

  return useMemo(
    () => ({
      session,
      setSell,
      setCost,
      setMonthSeason,
      addPeriod,
      updatePeriod,
      replacePeriods,
      removePeriod,
      loadDraft,
    }),
    [session, setSell, setCost, setMonthSeason, addPeriod, updatePeriod, replacePeriods, removePeriod, loadDraft],
  );
}
